import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useAuthStore } from '@/stores/auth';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { cn } from '@/lib/utils';

/**
 * Watermark — overlays the current student's ID + email diagonally across a
 * protected piece of content (lesson video, premium image, etc.).
 *
 * It's not a substitute for proper DRM, but it raises the friction of casual
 * screen-recording: the recorded clip carries the leaker's identity. The
 * pattern is repeated on a tilted grid so cropping a small region doesn't
 * remove all instances of the mark.
 *
 * Implementation notes:
 *   - We render the watermark in a separate absolutely-positioned layer with
 *     `pointer-events: none` so clicks pass through to the content.
 *   - The mark text is small (10px) and very low opacity (~12%) — enough to
 *     be picked up by recording but not distract a reader.
 *   - The watermark layer is built with `repeat(N, …)` grid so it scales
 *     fluidly with container size.
 *   - A tiny `key`-bound state forces re-render every ~30s. Combined with a
 *     timestamp in the text, this defeats a "save a single watermarked
 *     screenshot once" replay attack: each frame of a recording will show
 *     a slightly different mark.
 */
export type WatermarkProps = {
  children: ReactNode;
  /** Extra mark text appended after the user identifier. */
  context?: string;
  className?: string;
};

/**
 * WatermarkLayer — ТОЛЬКО слой с метками, без обёртки вокруг содержимого.
 *
 * Нужен там, где элемент сам уходит в полноэкранный режим: плеер разворачивает
 * СВОЙ контейнер, и знак, навешенный обёрткой снаружи, при развороте остаётся
 * за кадром. То есть исчезает ровно тогда, когда нужен — в полный экран и
 * переходят, чтобы снять запись экрана.
 *
 * Слой позиционируется абсолютно, поэтому родитель обязан быть relative.
 *
 * ⚠️ В ПРИЛОЖЕНИИ ЗНАКА НЕТ (D62, решение владельца; 26.09.2026, сборка 61). Там запись экрана закрывает
 * сама система — шторка на iOS (`ScreenGuardPlugin.swift`) и FLAG_SECURE на Android, — а почта ученика
 * поверх видео выглядела бы в магазине как слежка. Ветка сборки, не флаг: в dist-mobile знака нет вовсе.
 * Обёртка, а не ранний `return` в самом слое: хуки нельзя обходить условием (rules-of-hooks).
 */
export function WatermarkLayer(props: { className?: string; mark?: string }) {
  return IS_APP_BUILD ? null : <SiteWatermarkLayer {...props} />;
}

function SiteWatermarkLayer({ className, mark: given }: { className?: string; mark?: string }) {
  const user = useAuthStore((s) => s.user);
  const [tick, setTick] = useState(() => Date.now());
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  /**
   * ⚠️ СЕТКА СЧИТАЕТСЯ ОТ РАЗМЕРА, А НЕ ЗАДАНА ЧИСЛОМ.
   *
   * Здесь стояло жёстко 8 колонок на 6 строк — сколько бы места ни было. На
   * компьютере это выглядело правильно, а на телефоне восемь подписей в ряд
   * не помещались, наезжали друг на друга и превращались в сплошную кашу
   * поверх видео. Заказчик 07.09.2026: «на компе он норм, а на телефоне капец,
   * невозможно».
   *
   * Считаем по месту: одной подписи нужно около 160 точек по ширине и 120 по
   * высоте. На телефоне выходит две колонки, на широком экране — прежние восемь,
   * то есть на компьютере ничего не изменилось. Защита не слабеет: подписей
   * меньше, но каждая ЧИТАЕТСЯ, а нечитаемая каша не опознает никого.
   */
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const narrow = box.w > 0 && box.w < 520;
  const cols = Math.min(8, Math.max(2, Math.round(box.w / 160) || 3));
  const rows = Math.min(6, Math.max(2, Math.round(box.h / 120) || 3));

  // Подпись обычно собирается из текущего пользователя. Но на странице плеера
  // (она открыта в рамке и своей сессии не имеет) её присылает родитель —
  // тогда берём готовую.
  /**
   * ⚠️ НА УЗКОМ ЭКРАНЕ ПОДПИСЬ КОРОЧЕ — БЕЗ ПОЧТЫ.
   *
   * Полная подпись «8880DAEF • ADMIN@ONEBILIM.KZ • 2026-09-07T08:00» на телефоне
   * шире самого экрана, и рядом с ней ничего не разобрать. Опознать человека
   * хватает первых восьми знаков его номера — они есть в базе, и почта из них
   * находится за секунду. Дата со временем остаётся: без неё нельзя сказать,
   * когда сняли запись.
   */
  const mark =
    given ||
    [
      user?.id?.slice(0, 8) ?? 'anon',
      narrow ? '' : (user?.email ?? ''),
      new Date(tick).toISOString().slice(0, 16),
    ]
      .filter(Boolean)
      .join(' • ');

  const cells = Array.from({ length: cols * rows });

  return (
    <div
      ref={boxRef}
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 z-40 select-none overflow-hidden',
        className,
      )}
    >
      <div
        className="grid h-full w-full"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          transform: 'rotate(-22deg) scale(1.3)',
          transformOrigin: 'center',
        }}
      >
        {cells.map((_, i) => (
          <span
            key={i}
            className="flex items-center justify-center whitespace-nowrap font-mono uppercase tracking-wider"
            style={{
              // На телефоне подпись мельче и бледнее: там она занимает
              // заметно большую долю экрана, чем на компьютере.
              fontSize: narrow ? '8px' : '10px',
              color: `rgba(255, 255, 255, ${narrow ? 0.1 : 0.14})`,
              userSelect: 'none',
            }}
          >
            {mark}
          </span>
        ))}
      </div>
    </div>
  );
}

/** В приложении — только содержимое в той же обёртке, без знака (D62; пояснение у `WatermarkLayer`). */
export function Watermark(props: WatermarkProps) {
  return IS_APP_BUILD ? (
    <div className={cn('relative isolate', props.className)}>{props.children}</div>
  ) : (
    <SiteWatermark {...props} />
  );
}

function SiteWatermark({ children, className }: WatermarkProps) {
  const user = useAuthStore((s) => s.user);
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Show a short identifier — full ID is verbose, but the leading 8 chars
  // are unique enough across our user base to attribute a leak. We include
  // the email so the leaker recognises themselves immediately.
  const mark = [
    user?.id?.slice(0, 8) ?? 'anon',
    user?.email ?? '',
    new Date(tick).toISOString().slice(0, 16),
  ]
    .filter(Boolean)
    .join(' • ');

  // 8×6 grid of marks across the content — enough density that a typical
  // crop still leaves at least one full mark visible.
  const cells = Array.from({ length: 48 });

  return (
    <div className={cn('relative isolate', className)}>
      {children}

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10 select-none overflow-hidden"
      >
        <div
          className="grid h-full w-full"
          style={{
            gridTemplateColumns: 'repeat(8, 1fr)',
            gridTemplateRows: 'repeat(6, 1fr)',
            transform: 'rotate(-22deg) scale(1.3)',
            transformOrigin: 'center',
          }}
        >
          {cells.map((_, i) => (
            <span
              key={i}
              className="flex items-center justify-center text-[10px] font-mono uppercase tracking-wider"
              style={{
                color: 'rgba(26, 28, 28, 0.12)', // on-surface @ 12%
                userSelect: 'none',
              }}
            >
              {mark}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
